import { useApolloClient } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { Document, Packer } from 'docx'
import { saveAs } from 'file-saver'
import { ProjectType } from '../data/gql-gen/dashboard/graphql'
import {
  QUESTIONNAIRE,
  QuestionnaireData
} from '../data/gql/questionnaire/queries'
import {
  AudienceData,
  GET_AUDIENCES
} from '../data/gql/questionnaire/queries/audiences'
import { GET_FIELDWORK } from '../data/gql/questionnaire/queries/fieldwork'
import { GET_COUNTRIES_V2 } from '../data/gql/questionnaire/queries/getCountries'
import {
  DraftCustomAudience,
  FieldworkAudience,
  FieldworkAudienceType
} from '../data/model/audience'
import {
  DraftQuestionItem,
  DraftQuestionnaireEntry
} from '../data/model/questionnaire'
import {
  PROJECT_BY_SURVEY_ID,
  ProjectData
} from '../modules/Project/Project.query'
import { formatMarketLine } from '../modules/Questionnaire/MarketVisibility/useHiddenMarketsLabel'
import {
  MarketOption,
  useProjectMarkets
} from '../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { ResponseOptionsByQuestion } from '../modules/Questionnaire/Questionnaire.slice'
import {
  flattenAllEntries,
  flattenEntries,
  getResponseOptionsByQuestion
} from '../modules/Questionnaire/Questionnaire.utils'
import { applyTranslationOverlay } from '../modules/Translations/lib/previewOverlay'
import { createPreviewUrl } from '../utils/HelperFunctions'
import { sortBy } from '../utils/array/sortBy'
import { PanelSupplier, convertAudienceType } from '../utils/audienceHelper'
import {
  createQuestions,
  createQuestionsBehaviorSummary,
  createSummary
} from '../utils/surveyDocBuilder'
import {
  MarketExportMeta,
  PreviewLink,
  computeExportMarkets
} from '../utils/surveyDocBuilder/exportMarkets'
import { useOptionalWaveId } from './useOptionalWaveId'
import { useProjectId } from './useProjectId'
import { useSurveyId } from './useSurveyId'

const DEFULT_COUNTRY_NAME = 'UK'

interface ExportSurveyParams {
  projectName: string
  surveyId: string
  countryName: string
  previewUrl: string
  questionnaireEntries: DraftQuestionnaireEntry[]
  questionnaireAudienceEntries: DraftQuestionnaireEntry[]
  responseOptionsByQuestion: ResponseOptionsByQuestion
  includeAudienceQuestions: boolean
  // Authoritative multi-market flag (from project.projectType, resolved against
  // the freshly-queried project). Drives the summary's "Markets:" vs "Country:"
  // line and the audience-exclusion default upstream — never inferred from the
  // market list, which can be empty on a cold cache.
  isMultiMarket: boolean
  // The project's markets, used to render per-item "Hidden in: …" annotations
  // and the summary's market list. Empty for single-market surveys (no-op).
  allMarkets: MarketOption[]
  // Per-market preview links (multi-market). Absent → single `previewUrl`.
  previewLinks?: PreviewLink[]
}
const exportSurvey = async ({
  projectName,
  surveyId,
  countryName,
  previewUrl,
  questionnaireEntries,
  questionnaireAudienceEntries,
  responseOptionsByQuestion,
  includeAudienceQuestions,
  isMultiMarket,
  allMarkets,
  previewLinks
}: ExportSurveyParams) => {
  const pageContent = [
    ...(await createSummary({
      projectName,
      surveyId,
      countryName,
      previewUrl,
      isMultiMarket,
      markets: allMarkets,
      previewLinks
    }))
  ]

  const sortedEntries: DraftQuestionnaireEntry[] = sortBy(
    questionnaireEntries,
    { key: 'position' }
  )

  const sortedAudienceEntries: DraftQuestionnaireEntry[] = sortBy(
    questionnaireAudienceEntries,
    { key: 'position' }
  )

  // Multi-market exports ship the shared (master) questionnaire only: audience
  // questions differ per market, so printing one market's screener would be
  // misleading. The audience entries are still passed to the survey-question
  // builder below so display-logic references to them keep resolving.
  if (includeAudienceQuestions) {
    const audienceQuestionContent = await createQuestions({
      audienceEntries: sortedAudienceEntries,
      responseOptionsByQuestion,
      allMarkets
    })
    pageContent.push(...audienceQuestionContent)
  }

  const questionContent = await createQuestions({
    entries: sortedEntries,
    audienceEntries: sortedAudienceEntries,
    responseOptionsByQuestion,
    allMarkets
  })
  pageContent.push(...questionContent)

  pageContent.push(
    ...createQuestionsBehaviorSummary(flattenEntries(sortedEntries))
  )

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: 22,
            font: 'Arial',
            bold: true
          }
        }
      ],
      default: {
        document: {
          run: {
            font: 'Arial',
            size: 22
          }
        }
      }
    },
    sections: [
      {
        children: pageContent
      }
    ]
  })
  Packer.toBlob(doc).then((blob) => {
    saveAs(blob, `${projectName}.docx`)
  })
}

const useDownloadSurveyDoc = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  const client = useApolloClient()

  // The project's markets (base market first, then alphabetical), resolved via
  // the same hook the on-screen visibility indicators use so the exported doc's
  // "Hidden in: …" annotations and market list stay consistent with them. Used
  // for rendering only — the multi-market decision is taken from the freshly
  // queried project type below, not from this render-time list.
  const allMarkets = useProjectMarkets()

  return {
    exportSurvey: async (
      languageCode?: string,
      options?: { includeAudienceQuestions?: boolean }
    ) => {
      const { data: dataProject } = await client.query<ProjectData>({
        context: { clientName: 'dashboard' },
        query: PROJECT_BY_SURVEY_ID,
        variables: {
          surveyId
        }
      })

      const projectName = dataProject.project.name
      // Authoritative, load-aware multi-market check: read the project type from
      // the freshly-queried project (the same signal the rest of the app uses)
      // rather than inferring it from the render-time `allMarkets` hook, which
      // can be empty on a cold cache and would silently downgrade a multi-market
      // export to a single-market doc (wrong country, screener wrongly included,
      // no visibility annotations — with no error).
      const isMultiMarket =
        dataProject.project.projectType === ProjectType.MultiMarketV2

      // Multi-market master exports ship the shared questionnaire without the
      // per-market screener by default (audience questions differ per market);
      // callers may still override explicitly.
      const includeAudienceQuestions =
        options?.includeAudienceQuestions ?? !isMultiMarket
      let questionnaireEntries = [] as DraftQuestionnaireEntry[]
      let questionnaireAudienceEntries = [] as DraftQuestionnaireEntry[]
      let countryName = DEFULT_COUNTRY_NAME
      let panelSupplierCode = PanelSupplier.CUSTOM

      // try to get country name
      try {
        const { data: fieldworkData } = await client.query({
          context: { clientName: 'questionnaire' },
          query: GET_FIELDWORK,
          variables: {
            surveyId
          }
        })

        const fieldwork = fieldworkData.fieldwork

        if (fieldwork) {
          panelSupplierCode = convertAudienceType(
            fieldwork.audience[0].__typename
          )

          const { data: dataCountries } = await client.query({
            context: { clientName: 'questionnaire' },
            query: GET_COUNTRIES_V2,
            variables: { panelSupplierCode }
          })

          countryName =
            dataCountries.getCountriesV2?.find((country) => {
              return country.countryCode === fieldwork.locale.country
            })?.name ?? DEFULT_COUNTRY_NAME
        }
      } catch (e) {
        // leave countryName unchanged
      }

      // Multi-market: the export runs off the master survey, so the single
      // `previewUrl` below points at the base market only. Resolve each market's
      // own language(s) and panel supplier from its fieldwork, then scope the
      // doc to the exported language (Canada + France for French, not the UK
      // English base) and emit one preview link per in-scope market.
      let exportMarkets = allMarkets
      let previewLinks: PreviewLink[] | undefined
      if (isMultiMarket) {
        const marketMetaEntries = await Promise.all(
          allMarkets.map(
            async (market): Promise<[string, MarketExportMeta]> => {
              try {
                const { data } = await client.query({
                  context: { clientName: 'questionnaire' },
                  query: GET_FIELDWORK,
                  variables: { surveyId: market.surveyId }
                })
                const fw = data.fieldwork
                const allocated = (fw?.languageAllocations ?? []).map(
                  (allocation) => allocation.language
                )
                const languages =
                  allocated.length > 0
                    ? allocated
                    : fw?.locale.language
                      ? [fw.locale.language]
                      : []
                const panel = fw?.audience[0]
                  ? convertAudienceType(fw.audience[0].__typename)
                  : PanelSupplier.CUSTOM
                return [market.surveyId, { languages, panel }]
              } catch {
                return [
                  market.surveyId,
                  { languages: [], panel: PanelSupplier.CUSTOM }
                ]
              }
            }
          )
        )
        const marketMeta = new Map(marketMetaEntries)

        const scopedMarkets = computeExportMarkets(
          allMarkets,
          marketMeta,
          languageCode
        )
        if (
          languageCode &&
          scopedMarkets.length === 0 &&
          allMarkets.length > 0
        ) {
          datadogLogs.logger.warn(
            'Survey export: no market uses the selected language; falling back to all markets',
            { languageCode }
          )
        }
        exportMarkets = scopedMarkets.length > 0 ? scopedMarkets : allMarkets

        previewLinks = exportMarkets.map((market) => ({
          label: formatMarketLine(market),
          url: createPreviewUrl(
            market.surveyId,
            marketMeta.get(market.surveyId)?.panel ?? PanelSupplier.CUSTOM
          )
        }))
      }

      let responseOptionsByQuestion: ResponseOptionsByQuestion = {}

      // Audience translations are stored on `DraftCustomAudience` and reached
      // through GET_AUDIENCES, not on the questionnaire query's
      // `audienceEntries[].entryItem` projection (which carries
      // `translations: null` even when audience translations exist). Fetch the
      // audience-side data here and use it to backfill the questionnaire view
      // before the overlay runs. Failures are swallowed so the export still
      // ships master text rather than blocking entirely.
      let audienceMembersById = new Map<string, FieldworkAudience>()
      try {
        const { data: audienceData } = await client.query<AudienceData>({
          context: { clientName: 'questionnaire' },
          query: GET_AUDIENCES,
          variables: { projectId, surveyId, waveId },
          fetchPolicy: 'no-cache'
        })
        audienceMembersById = new Map(
          audienceData.draftAudience.members.map((m) => [m.id, m])
        )
      } catch {
        audienceMembersById = new Map()
      }

      // Copy translations from the audience-side `DraftCustomAudience` (and
      // its `criterionOptions[]`) into the matching questionnaire-side
      // audience entry so the existing `applyTranslationOverlay` can resolve
      // them like it does for survey entries. Translations now live on
      // `question.translations` / `responseOption.translations` on both sides,
      // so the synthesizer reads from and writes to those inner paths.
      const mergeAudienceTranslations = (
        entry: DraftQuestionnaireEntry
      ): DraftQuestionnaireEntry => {
        const member = audienceMembersById.get(entry.id)
        if (!member || member.memberType !== FieldworkAudienceType.Custom) {
          return entry
        }
        const audienceItem = member.memberItem as DraftCustomAudience
        const item = entry.entryItem as DraftQuestionItem
        const optionTranslations = new Map<
          string,
          { language: string; value: string }[]
        >()
        audienceItem.criterionOptions.forEach((opt) => {
          const translations = opt.responseOption.translations
          if (translations && translations.length > 0) {
            optionTranslations.set(
              opt.responseOption.responseOptionId,
              translations.map((t) => ({
                language: t.language,
                value: t.value
              }))
            )
          }
        })
        return {
          ...entry,
          entryItem: {
            ...item,
            question: item.question
              ? {
                  ...item.question,
                  translations:
                    audienceItem.question.translations?.map((t) => ({
                      language: t.language,
                      text: t.text,
                      textStyling: t.textStyling ?? null,
                      __typename: 'QuestionTranslation' as const
                    })) ?? null
                }
              : item.question,
            responseOptions: item.responseOptions.map((opt) => {
              const optId = opt.responseOption?.responseOptionId
              const translations = optId
                ? optionTranslations.get(optId)
                : undefined
              if (!translations || !opt.responseOption) return opt
              return {
                ...opt,
                responseOption: {
                  ...opt.responseOption,
                  translations: translations.map((t) => ({
                    language: t.language,
                    value: t.value,
                    __typename: 'ResponseOptionTranslation' as const
                  }))
                }
              }
            })
          }
        } as DraftQuestionnaireEntry
      }

      // try to get questionnaire entries
      try {
        const { data: questionnaireData } =
          await client.query<QuestionnaireData>({
            context: { clientName: 'questionnaire' },
            query: QUESTIONNAIRE,
            // No `languageCode` here on purpose: the response shape is the
            // same regardless (master text + the `translations[]` array on
            // every entry), and we resolve the per-language view client-side
            // via `applyTranslationOverlay`. Passing the language argument
            // would double-process the data (server overlay + our overlay)
            // and force a separate cache slice for every language.
            variables: { projectId, surveyId, waveId },
            fetchPolicy: 'no-cache'
          })

        const { draftQuestionnaire } = questionnaireData

        // @todo Legacy eslint violation – fix this when editing
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (draftQuestionnaire) {
          responseOptionsByQuestion = getResponseOptionsByQuestion(
            flattenAllEntries(draftQuestionnaire)
          )
          const overlay = (
            entries: ReadonlyArray<DraftQuestionnaireEntry>
          ): DraftQuestionnaireEntry[] =>
            languageCode
              ? entries.map((e) =>
                  applyTranslationOverlay(e, languageCode, {
                    fallbackToMaster: true
                  })
                )
              : entries.slice()
          questionnaireEntries = overlay(draftQuestionnaire.entries)
          // For audience entries we first merge translations from the
          // audience-side query (the questionnaire response leaves
          // `audienceEntries[].entryItem.translations` null), then run the
          // same overlay so the doc builder sees translated screener text.
          questionnaireAudienceEntries = overlay(
            draftQuestionnaire.audienceEntries.map(mergeAudienceTranslations)
          )
        }
      } catch (e) {
        // leave questionnaireEntries unchanged
      }

      const filenameSuffix = languageCode ? `_${languageCode}` : ''
      exportSurvey({
        projectName: `${projectName}${filenameSuffix}`,
        surveyId,
        countryName,
        previewUrl: createPreviewUrl(surveyId, panelSupplierCode),
        previewLinks,
        questionnaireEntries,
        questionnaireAudienceEntries,
        responseOptionsByQuestion,
        includeAudienceQuestions,
        isMultiMarket,
        allMarkets: exportMarkets
      })
    }
  }
}

export default useDownloadSurveyDoc
